/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Divider } from "primereact/divider";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  Sidebar,
  useFuncionalidad,
  tituloBase,
  TablaFiltrable
} from "../../shared";
import TiposMoneda from "./TiposMoneda";

// Los dos textos de la propuesta admiten acentos escritos entre corchetes. No
// se usa HTML a propósito: esto lo escribe un admin y lo lee un cliente, y
// renderizar HTML de un campo editable abriría un XSS en el portal.
const NOTAS_FORMATO =
  "Están disponibles los tags [b] Resaltado [/b], [i] Cursiva [/i], " +
  "[r] Rojo [/r], [u] Subrayado [/u] así como [url=https://link.com] Link [/url]. " +
  "[li] Viñeta [/li] e [ind] Indentado [/ind] ocupan su propia línea y se " +
  "combinan: [ind][li] Ítem indentado [/li][/ind]. " +
  "Una línea en blanco separa párrafos (abrí y cerrá cada tag dentro del mismo párrafo). " +
  "En el PDF del contrato salen con formato si el template usa los tags " +
  "[[@propuesta.textoImportanteFormato]] y [[@propuesta.textoAceptacionFormato]].";

export default function GeneralConfig() {
  const conJuguetesDni = useFuncionalidad("JUGUETES_DNI");
  const [loader, setLoader] = useState(true);
  const [configuracion, setConfiguracion] = useState();
  const [sideBarEditar, setSideBarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const toast = useRef(null);

  useEffect(() => {
    document.title = "Configuración general";
    accessAPI(
      "GET",
      "admin/config/general",
      null,
      (respuesta) => {
        setConfiguracion(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar configuración",
          sticky: true,
        });
        setLoader(false);
      }
    );
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function guardarConfiguracion(contenidoCampos) {
    setLoaderEditar(true);
    accessAPI(
      "PATCH",
      "admin/config/general",
      contenidoCampos,
      (respuesta) => {
        setConfiguracion(respuesta);
        setSideBarEditar(false);
        setLoaderEditar(false);
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al guardar configuración",
          sticky: true,
        });
      }
    );
  }

  function getEditarFormConfig() {
    return {
      secciones: [
        {
          campos: [
            // Configuración propia de la certificación de juguetes con DNI.
            ...(conJuguetesDni
              ? [
                  {
                    nombre: "certificadotipojuguetesid",
                    tipoitemformularioid: 5,
                    etiqueta: "Tipo de certificado de juguetes",
                    opciones: "admin/config/certificadotipo",
                    respuesta: configuracion?.certificadotipojuguetesid,
                    sinOpcionInicial: true,
                  },
                  {
                    nombre: "templateCertificadoFamiliaJuguetesId",
                    tipoitemformularioid: 5,
                    etiqueta: "Template de certificado de familias de juguetes",
                    opciones: "admin/template?dropdown=true",
                    respuesta: configuracion?.templateCertificadoFamiliaJuguetesId,
                    sinOpcionInicial: true,
                  },
                ]
              : []),
            {
              nombre: "templatePropuestaComercialId",
              tipoitemformularioid: 5,
              etiqueta: "Template de propuesta comercial",
              opciones: "admin/template?dropdown=true",
              respuesta: configuracion?.templatePropuestaComercialId,
              sinOpcionInicial: true,
            },
            {
              nombre: "templateMarcaId",
              tipoitemformularioid: 5,
              etiqueta: "Template de marca",
              opciones: "admin/template?dropdown=true",
              respuesta: configuracion?.templateMarcaId,
              sinOpcionInicial: true,
            },
            {
              nombre: "templateEtiquetaMuestraId",
              tipoitemformularioid: 5,
              etiqueta: "Template de etiqueta de muestra",
              opciones: "admin/template?dropdown=true",
              respuesta: configuracion?.templateEtiquetaMuestraId,
              sinOpcionInicial: true,
            },
            {
              nombre: "templateEtiquetaCajaId",
              tipoitemformularioid: 5,
              etiqueta: "Template de etiqueta de caja",
              opciones: "admin/template?dropdown=true",
              respuesta: configuracion?.templateEtiquetaCajaId,
              sinOpcionInicial: true,
            },
            {
              nombre: "templateEtiquetaEnsayoId",
              tipoitemformularioid: 5,
              etiqueta: "Template de etiqueta de ensayo",
              opciones: "admin/template?dropdown=true",
              respuesta: configuracion?.templateEtiquetaEnsayoId,
              sinOpcionInicial: true,
            },
            ...(conJuguetesDni
              ? [
                  {
                    nombre: "precioEnsayoJuguetes",
                    tipoitemformularioid: 1,
                    etiqueta: "Precio ensayo juguetes",
                    respuesta: configuracion?.precioEnsayoJuguetes?.toString() ?? "",
                  },
                ]
              : []),
            {
              nombre: "adendaFactura",
              tipoitemformularioid: 11,
              etiqueta: "Adenda a la factura",
              respuesta: configuracion?.adendaFactura ?? "",
            },
            {
              nombre: "textoPropuestaImportante",
              tipoitemformularioid: 11,
              etiqueta: "Propuesta comercial · Importante, leer con atención",
              notas: NOTAS_FORMATO,
              respuesta: configuracion?.textoPropuestaImportante ?? "",
            },
            {
              nombre: "textoPropuestaAceptacion",
              tipoitemformularioid: 11,
              etiqueta: "Propuesta comercial · Aceptación",
              notas: NOTAS_FORMATO,
              respuesta: configuracion?.textoPropuestaAceptacion ?? "",
            },
          ],
        },
      ],
      botones: [
        {
          etiqueta: "Guardar",
          callback: guardarConfiguracion,
          validar: true,
        },
      ],
    };
  }

  const filas = configuracion
    ? [
        ...(conJuguetesDni
          ? [
              {
                configuracion: "Tipo de certificado de juguetes",
                valor: configuracion.certificadotipojuguetes
                  ? `${configuracion.certificadotipojuguetes.procesotipo?.nombre} - ${configuracion.certificadotipojuguetes.nombre}`
                  : "—",
              },
              {
                configuracion: "Template de certificado de familias de juguetes",
                valor:
                  configuracion.TemplateCertificadoFamiliaJuguetes?.nombre || "—",
              },
            ]
          : []),
        {
          configuracion: "Template de propuesta comercial",
          valor: configuracion.templatePropuestaComercial?.nombre || "—",
        },
        {
          configuracion: "Template de marca",
          valor: configuracion.templateMarca?.nombre || "—",
        },
        {
          configuracion: "Template de etiqueta de muestra",
          valor:
            (configuracion.templateEtiquetaMuestra?.nombre || "—") +
            (configuracion.templateEtiquetaMuestra &&
            (configuracion.templateEtiquetaMuestra.tipo !== "SCRIPT" ||
              !configuracion.templateEtiquetaMuestra.archivoscriptid)
              ? " ⚠ requiere tipo SCRIPT con script cargado"
              : ""),
        },
        {
          configuracion: "Template de etiqueta de caja",
          valor:
            (configuracion.templateEtiquetaCaja?.nombre || "—") +
            (configuracion.templateEtiquetaCaja &&
            (configuracion.templateEtiquetaCaja.tipo !== "SCRIPT" ||
              !configuracion.templateEtiquetaCaja.archivoscriptid)
              ? " ⚠ requiere tipo SCRIPT con script cargado"
              : ""),
        },
        {
          configuracion: "Template de etiqueta de ensayo",
          valor:
            (configuracion.templateEtiquetaEnsayo?.nombre || "—") +
            (configuracion.templateEtiquetaEnsayo &&
            (configuracion.templateEtiquetaEnsayo.tipo !== "SCRIPT" ||
              !configuracion.templateEtiquetaEnsayo.archivoscriptid)
              ? " ⚠ requiere tipo SCRIPT con script cargado"
              : ""),
        },
        ...(conJuguetesDni
          ? [
              {
                configuracion: "Precio ensayo juguetes",
                valor: configuracion.precioEnsayoJuguetes ?? "—",
              },
            ]
          : []),
        {
          configuracion: "Adenda a la factura",
          valor: configuracion.adendaFactura || "—",
        },
        {
          configuracion: "Propuesta comercial · Importante",
          valor: configuracion.textoPropuestaImportante ? "Configurado" : "—",
        },
        {
          configuracion: "Propuesta comercial · Aceptación",
          valor: configuracion.textoPropuestaAceptacion ? "Configurado" : "—",
        },
      ]
    : [];

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={sideBarEditar}
        onHide={() => setSideBarEditar(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar configuración</Titulo>
        {configuracion && (
          <Formulario
            key={formKey}
            config={getEditarFormConfig()}
            loader={loaderEditar}
          />
        )}
      </Sidebar>

      {loader && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em",
          }}
        >
          <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
            <Skeleton width="2rem" height="2rem" shape="circle" />
            <Skeleton width="20rem" height="2rem" />
          </div>
          <Card>
            <Skeleton width="100%" height="3rem" />
            {[...Array(4)].map((_, i) => (
              <Skeleton
                key={i}
                width="100%"
                height="3.5rem"
                css={{ marginTop: "0.3rem" }}
              />
            ))}
          </Card>
        </div>
      )}

      {!loader && configuracion && (
        <div>
          <Titulo
            urlVolver="/config"
            botones={
              <Boton
                transparente
                callback={() => {
                  setFormKey((k) => k + 1);
                  setSideBarEditar(true);
                }}
              >
                Editar
              </Boton>
            }
          >
            Configuración general
          </Titulo>
          <Card>
            <TablaFiltrable conFiltros={false}
              value={filas}
              stripedRows
              emptyMessage="No hay configuraciones"
              nombreArchivo="configuracion_general"
            >
              <Column header="Configuración" field="configuracion" />
              <Column header="Valor" field="valor" />
            </TablaFiltrable>
          </Card>
        </div>
      )}

      <Divider />
      <TiposMoneda />
    </>
  );
}
